import { useState } from 'react';
import { useClusterApi } from '../../api/ClusterApiProvider';
import { useAsync } from '../../api/useAsync';
import type {
  ClusterNode,
  InstallKey,
  InstallRole,
  JoinToken,
  OfferedResource,
} from '../../api/types';
import type { ApiError } from '../../api/errors';
import { useClusterSession } from '../../auth/ClusterSessionProvider';
import { mayManageCatalogue } from '../../auth/clusterRole';
import { useI18n } from '../../i18n/useI18n';
import { Button, Card, Loading, PageHead } from '../../ui/primitives';
import { FilterBar, SegmentedFilter } from '../../ui/form';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { toApiError } from '../../api/errors';
import { DataTable } from '../../ui/DataTable';
import { CopyField } from '../../ui/CopyField';
import { AgoUnix } from '../../ui/Ago';
import { IconClaimed } from '../../ui/icons';
import { StatusBadge } from '../../ui/StatusBadge';
import { useToast } from '../../ui/Toast';
import { kindLabelKey } from '../../ui/ResourceTable';
import { ResourceDialog } from './ResourceDialog';
import { declarableAsIs, pendingOffers } from './offered';
import { DEFAULT_NODE_FILTER, NODE_FILTERS, nodeCounts, nodesFor } from './nodeFilter';
import { shortName } from './nodeName';
import type { NodeFilter } from './nodeFilter';
import type { WriteOutcome } from './catalogWrite';

/**
 * Whole minutes a key has left, never below one.
 *
 * Rounded down, so a screen saying "fifteen minutes" is not saying it about
 * fourteen and a half — and floored at one, because "0 minutes" reads as
 * expired while the key still works.
 */
function minutesLeft(expiresAt: string): number {
  const left = (Date.parse(expiresAt) - Date.now()) / 60_000;
  return Math.max(1, Math.floor(left));
}

/** Stable identity, so the table is not handed a new array every render. */
const NO_NODES: ClusterNode[] = [];
const NO_OFFERS: OfferedResource[] = [];

/**
 * What this cluster has enrolled, and the one thing an administrator does to
 * that list: let another connector in.
 *
 * **Read from the cluster, not from a record of ours.** What is enrolled is the
 * cluster's own fact; a node somebody joined by hand appears here for the same
 * reason, and a row we kept beside it would be a second answer that drifts.
 *
 * Two things this screen is careful to say rather than imply:
 *
 * * a **claimed name** is what the node said about itself. A token is minted
 *   for a role and never for a machine, so there is nothing in a name for the
 *   cluster to check. It is shown because it is the only human-readable handle
 *   a node has, and it is marked because showing it plainly would claim more
 *   than the registry knows;
 * * **revoking closes renewal, not the connection.** The leaf that node holds
 *   stays verifiable until it expires. Anybody expecting a session to drop is
 *   expecting the wrong thing, and finding that out afterwards is worse.
 *
 * The token is shown once, here, and never again: nothing stores it. Minting
 * another is one press, which is the honest answer to "I lost it" — the other
 * answer would mean this console kept a credential.
 */
export function NodesScreen() {
  const api = useClusterApi();
  const { t, formatDateTime } = useI18n();
  const { session } = useClusterSession();
  const toast = useToast();

  const nodes = useAsync((signal) => api.listNodes(signal), 'nodes');
  const offers = useAsync((signal) => api.listOffered(signal), 'offered');
  const [minted, setMinted] = useState<JoinToken | null>(null);
  const [minting, setMinting] = useState(false);
  const [install, setInstall] = useState<InstallKey | null>(null);
  const [preparing, setPreparing] = useState<InstallRole | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [filter, setFilter] = useState<NodeFilter>(DEFAULT_NODE_FILTER);
  const [forgetting, setForgetting] = useState<string | null>(null);
  const [declaring, setDeclaring] = useState<OfferedResource | null>(null);
  const [notTold, setNotTold] = useState<string | null>(null);

  const mayManage = mayManageCatalogue(session);
  const actorName = session?.actor.subject ?? '';
  const all: ClusterNode[] = nodes.status === 'ready' ? nodes.value : NO_NODES;
  const rows = nodesFor(filter, all);
  const counts = nodeCounts(all);
  const allOffers: OfferedResource[] = offers.status === 'ready' ? offers.value : NO_OFFERS;
  // Declared offers leave the section, and the whole list is still counted:
  // "nothing offered" and "all of it declared" are different situations, and
  // only one of them means a connector is missing.
  const undeclared = pendingOffers(allOffers);

  function declared(id: string, outcome: WriteOutcome) {
    setDeclaring(null);
    // Declared here, not delivered to the cluster: the offer is still
    // undeclared as far as the cluster is concerned, so the row stays and the
    // reason for it is said out loud.
    setNotTold(outcome.done === 'declared' ? id : null);
    offers.reload();
    if (outcome.done === 'delivered') toast(t('toast.resourceDeclared', { id }));
  }

  async function mint() {
    setMinting(true);
    setFailure(null);
    try {
      setMinted(await api.mintJoinToken());
    } catch (error) {
      setFailure(toApiError(error));
    } finally {
      setMinting(false);
    }
  }

  async function prepare(role: InstallRole) {
    setPreparing(role);
    setFailure(null);
    // One of the two at a time: two commands on a screen, each good for a
    // quarter of an hour, is a screen somebody pastes the wrong one from.
    setMinted(null);
    try {
      setInstall(await api.mintInstallKey(role));
    } catch (error) {
      setFailure(toApiError(error));
    } finally {
      setPreparing(null);
    }
  }

  async function forget(node: ClusterNode) {
    setForgetting(node.id);
    setFailure(null);
    try {
      await api.deleteNode(node.id);
      toast(t('nodes.forgotten', { id: node.id }));
      nodes.reload();
    } catch (error) {
      // The cluster's own sentence — "too recent", "not revoked" — says what to
      // do next, which is why it is shown rather than replaced.
      setFailure(toApiError(error));
    } finally {
      setForgetting(null);
    }
  }

  async function revoke(node: ClusterNode) {
    setFailure(null);
    try {
      await api.revokeNode(node.id);
      toast(t('nodes.revoked', { id: node.id }));
      nodes.reload();
    } catch (error) {
      setFailure(toApiError(error));
    }
  }

  return (
    <div className="content-inner">
      <PageHead
        title={t('nodes.title')}
        sub={t('nodes.subtitle')}
        actions={
          mayManage ? (
            <>
              <Button
                variant="primary"
                onClick={() => void prepare('connector')}
                disabled={preparing !== null}
              >
                {preparing === 'connector'
                  ? `${t('nodes.install.working')}…`
                  : t('nodes.install')}
              </Button>
              <Button
                onClick={() => void prepare('client')}
                disabled={preparing !== null}
              >
                {preparing === 'client' ? `${t('nodes.install.working')}…` : t('nodes.install.client')}
              </Button>
              <Button onClick={() => void mint()} disabled={minting}>
                {minting ? `${t('nodes.mint.working')}…` : t('nodes.mint')}
              </Button>
            </>
          ) : null
        }
      />

      {failure !== null && (
        <div style={{ marginBottom: 18 }}>
          <ErrorAlert error={failure} />
        </div>
      )}

      {install !== null && (
        <div style={{ marginBottom: 18 }}>
          <Alert tone="info" title={t('nodes.install.title')}>
            <p>
              {t(
                install.role === 'connector'
                  ? 'nodes.install.body.connector'
                  : 'nodes.install.body.client',
                { ttl: String(minutesLeft(install.expires_at)) },
              )}
            </p>
            <div style={{ marginTop: 10 }}>
              <CopyField value={install.command} label={t('nodes.install.label')} />
            </div>
          </Alert>
        </div>
      )}

      {minted !== null && (
        <div style={{ marginBottom: 18 }}>
          <Alert tone="warn" title={t('nodes.token.title')}>
            <p>{t('nodes.token.body', { ttl: String(Math.round(minted.ttl_secs / 60)) })}</p>
            <div style={{ marginTop: 10 }}>
              <CopyField value={minted.token} label={t('nodes.token.label')} />
            </div>
          </Alert>
        </div>
      )}

      <FilterBar
        filters={
          <SegmentedFilter<NodeFilter>
            legend={t('nodes.filter.label')}
            value={filter}
            options={NODE_FILTERS.map((value) => ({
              value,
              label: t(`nodes.filter.${value}`),
              count: counts[value],
            }))}
            onChange={setFilter}
          />
        }
      />

      <Card>
        {nodes.status === 'loading' ? (
          <Loading />
        ) : nodes.status === 'error' ? (
          <ErrorAlert error={nodes.error} />
        ) : (
          <DataTable
            caption={t('nodes.title')}
            rows={rows}
            // What the registry holds before the tab narrows it: without it an
            // empty tab would read as an empty fleet.
            total={all.length}
            rowKey={(node) => node.id}
            // An empty registry and an empty tab are different answers: the
            // first says no node has joined, the second that none is in this
            // state — and only the first is somebody's cue to mint a token.
            empty={{ title: t('nodes.none'), body: t('nodes.none.body') }}
            noMatch={{ title: t('nodes.noMatch'), body: t('nodes.noMatch.body') }}
            columns={[
              {
                // A mark, not a column of text. That a name is the node's own
                // claim is true of every named row here, so saying it in words
                // beside each one is a sentence repeated down the page — and it
                // widened the table by the length of that sentence.
                key: 'claimed',
                header: '',
                cell: (node) =>
                  node.claimed_name === undefined ? null : (
                    <span className="muted" title={t('nodes.name.claimed.why')}>
                      <IconClaimed />
                    </span>
                  ),
              },
              {
                key: 'name',
                header: t('nodes.col.name'),
                cell: (node) =>
                  node.claimed_name === undefined ? (
                    <span className="muted">{t('nodes.name.none')}</span>
                  ) : (
                    // The whole name is on the cell, so folding it costs
                    // nothing: what is cut is still one hover away.
                    <span className="mono" title={node.claimed_name}>
                      {shortName(node.claimed_name)}
                    </span>
                  ),
              },
              {
                // A badge, like a resource's kind in the catalogue: a role is a
                // fixed set of words the cluster chose from, and a reader picks
                // one out of a column of them by its shape.
                key: 'role',
                header: t('nodes.col.role'),
                cell: (node) => <span className="chip">{node.role}</span>,
              },
              {
                key: 'id',
                header: t('nodes.col.id'),
                cell: (node) => <span className="mono">{node.id}</span>,
              },
              {
                // "4 days ago", with the instant itself on hover. Which day a
                // node joined is rarely the question; how long ago almost
                // always is.
                key: 'enrolled',
                header: t('nodes.col.enrolled'),
                cell: (node) => <AgoUnix at={node.enrolled_at} />,
              },
              {
                key: 'state',
                header: t('nodes.col.state'),
                cell: (node) =>
                  node.revoked_at === undefined ? (
                    <StatusBadge tone="ok" label={t('nodes.state.active')} />
                  ) : (
                    <StatusBadge
                      tone="deny"
                      label={t('nodes.state.revoked')}
                      hint={formatDateTime(new Date(node.revoked_at * 1000).toISOString())}
                    />
                  ),
              },
              ...(mayManage
                ? [
                    {
                      key: 'act',
                      header: '',
                      align: 'end' as const,
                      cell: (node: ClusterNode) =>
                        node.revoked_at === undefined ? (
                          <Button variant="subtle" size="sm" onClick={() => void revoke(node)}>
                            {t('nodes.revoke')}
                          </Button>
                        ) : (
                          // Forgetting the row, which the cluster allows a week
                          // after the revocation. Offered on every revoked row
                          // rather than only the eligible ones: whether a week
                          // has passed is the cluster's arithmetic, and a
                          // console doing it in parallel would disagree with it
                          // by a clock skew.
                          <Button
                            variant="subtle"
                            size="sm"
                            onClick={() => void forget(node)}
                            disabled={forgetting === node.id}
                          >
                            {forgetting === node.id
                              ? `${t('nodes.forget.working')}…`
                              : t('nodes.forget')}
                          </Button>
                        ),
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Card>

      <div style={{ marginTop: 18 }}>      </div>

      <div style={{ marginTop: 28 }}>
        <PageHead title={t('offered.title')} sub={t('offered.subtitle')} />

        {notTold !== null && (
          <div style={{ marginBottom: 18 }}>
            <Alert tone="warn" title={t('catalog.notTold.title')} live>
              <p>{t('catalog.notTold.written', { id: notTold })}</p>
            </Alert>
          </div>
        )}

        <Card>
          {offers.status === 'loading' ? (
            <Loading />
          ) : offers.status === 'error' ? (
            <ErrorAlert error={offers.error} />
          ) : (
            <DataTable
              caption={t('offered.title')}
              rows={undeclared}
              rowKey={(offer) => `${offer.node_id}/${offer.resource_id}`}
              empty={
                allOffers.length === 0
                  ? { title: t('offered.none'), body: t('offered.none.body') }
                  : { title: t('offered.allDeclared'), body: t('offered.allDeclared.body') }
              }
              noMatch={{ title: t('offered.none'), body: t('offered.none.body') }}
              columns={[
                {
                  // The same mark as in the registry, for the same reason: what
                  // a connector offers is its own statement, true of every row
                  // here, and a sentence repeated down the page is a wider
                  // table and nothing else.
                  key: 'claimed',
                  header: '',
                  cell: () => (
                    <span className="muted" title={t('offered.claimed.why')}>
                      <IconClaimed />
                    </span>
                  ),
                },
                {
                  key: 'resource',
                  header: t('offered.col.resource'),
                  cell: (offer: OfferedResource) => (
                    <>
                      <span className="mono" title={offer.resource_id}>
                        {shortName(offer.resource_id)}
                      </span>{' '}
                      {declarableAsIs(offer.resource_id) ? null : (
                        <span className="chip" title={t('offered.unusable.why')}>
                          {t('offered.unusable')}
                        </span>
                      )}
                    </>
                  ),
                },
                {
                  key: 'kind',
                  header: t('offered.col.kind'),
                  cell: (offer: OfferedResource) => {
                    // A kind neither side knows reads as the word the cluster
                    // printed, rather than being mapped onto the nearest one we
                    // do know — which would be this console inventing a fact.
                    const label = kindLabelKey(offer.kind);
                    return <span className="chip">{label === null ? offer.kind : t(label)}</span>;
                  },
                },
                {
                  key: 'node',
                  header: t('offered.col.node'),
                  cell: (offer: OfferedResource) => {
                    const named = offer.claimed_name ?? offer.node_id;
                    return (
                      <span className="mono" title={named}>
                        {shortName(named)}
                      </span>
                    );
                  },
                },
                {
                  key: 'offered',
                  header: t('offered.col.offered'),
                  cell: (offer: OfferedResource) => <AgoUnix at={offer.offered_at} />,
                },
                ...(mayManage
                  ? [
                      {
                        key: 'act',
                        header: '',
                        align: 'end' as const,
                        // Declaring an id the catalogue cannot hold is not
                        // refused so much as pointless: the row would be written
                        // under a different id from the one being offered, and
                        // the two would never meet. So the button is not there,
                        // and what to do instead is said where the row is.
                        cell: (offer: OfferedResource) =>
                          declarableAsIs(offer.resource_id) ? (
                            <Button
                              variant="subtle"
                              size="sm"
                              onClick={() => {
                                setDeclaring(offer);
                              }}
                            >
                              {t('offered.declare')}
                            </Button>
                          ) : (
                            <span className="muted">{t('offered.unusable.fix')}</span>
                          ),
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </Card>

        <div style={{ marginTop: 18 }}>        </div>
        <div style={{ marginTop: 12 }}>        </div>
      </div>

      {declaring !== null && (
        <ResourceDialog
          offer={{ resource_id: declaring.resource_id, kind: declaring.kind }}
          actorName={actorName}
          onClose={() => {
            setDeclaring(null);
          }}
          onWritten={declared}
        />
      )}
    </div>
  );
}
